import { useState } from 'react';
import { ArrowRight, Plus, Minus, AlertTriangle, Maximize2 } from 'lucide-react';

import { DiffListModal } from './DiffListModal';

const LONG_TEXT_THRESHOLD = 120;

// Truncate a long string for the compact preview.
const truncate = (s, n = 120) => {
  if (s == null) return '—';
  const str = String(s);
  return str.length <= n ? str : str.slice(0, n) + '…';
};

const fmtValue = (field, v) => {
  if (v == null) return '—';
  if (field === 'price')
    return `${Number(v).toLocaleString('fr-FR')} €`;
  if (Array.isArray(v)) return v.join(', ');
  return truncate(String(v));
};

const isLongScalar = (a, b) => {
  const la = a == null ? 0 : String(a).length;
  const lb = b == null ? 0 : String(b).length;
  return la > LONG_TEXT_THRESHOLD || lb > LONG_TEXT_THRESHOLD;
};

// One diff row inside DiffDrawer — handles value/text/list_add/list_diff.
export const DiffField = ({
  diff,
  currentValue,
  newValue,
  isTicked,
  disabled = false,
  onToggle,
}) => {
  const { field, type, changed, added = [], removed = [], manual_edit } = diff;

  const isList = type === 'list_add' || type === 'list_diff';
  const [showFull, setShowFull] = useState(false);

  const scalarIsLong = !isList && changed && isLongScalar(currentValue, newValue);
  const listHasOverflow = isList && (added.length > 8 || removed.length > 8);

  return (
    <div
      data-testid={`diff-field-${field}`}
      className={
        'flex flex-col gap-2 p-3 rounded border ' +
        (!changed
          ? 'border-gray-100 bg-lightestGrayColor opacity-60'
          : manual_edit
          ? 'border-orangeColor/50 bg-orangeColor/5'
          : 'border-gray-200 bg-white')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid={`diff-field-checkbox-${field}`}
              type="checkbox"
              checked={!!isTicked}
              disabled={disabled || !changed}
              onChange={(e) => onToggle(field, e.target.checked)}
              className="accent-purpleColor w-4 h-4"
              aria-label={`Apply ${field}`}
            />
            <span className="text-sm font-medium text-blackColor capitalize">
              {field.replace(/_/g, ' ')}
            </span>
          </label>
          {!changed && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-lightGrayColor text-darkGrayColor">
              unchanged
            </span>
          )}
          {manual_edit && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-orangeColor/15 text-orangeColor">
              <AlertTriangle size={10} /> manual edit
            </span>
          )}
        </div>
        {(scalarIsLong || listHasOverflow) && (
          <button
            type="button"
            data-testid={`diff-field-view-full-${field}`}
            onClick={() => setShowFull(true)}
            className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-purpleColor/30 text-purpleColor hover:bg-purpleColor/5"
          >
            <Maximize2 size={11} /> View full
          </button>
        )}
      </div>

      {changed && !isList && (
        <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 text-xs">
          <div className="p-2 rounded bg-redColor/5 text-blackColor border border-redColor/20">
            <div className="text-[10px] uppercase text-redColor mb-0.5">Current</div>
            <div className="whitespace-pre-wrap break-words">
              {fmtValue(field, currentValue)}
            </div>
          </div>
          <div className="pt-3">
            <ArrowRight size={14} className="text-darkGrayColor" />
          </div>
          <div className="p-2 rounded bg-greenColor/5 text-blackColor border border-greenColor/20">
            <div className="text-[10px] uppercase text-greenColor mb-0.5">New</div>
            <div className="whitespace-pre-wrap break-words">
              {fmtValue(field, newValue)}
            </div>
          </div>
        </div>
      )}

      {changed && isList && (
        <div className="flex flex-col gap-2 text-xs">
          {added.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="flex items-center gap-1 text-greenColor mr-1">
                <Plus size={12} /> {added.length}
              </span>
              {added.slice(0, 8).map((v, i) => (
                <span
                  key={i}
                  className="px-1.5 py-0.5 rounded bg-greenColor/10 text-greenColor border border-greenColor/20 truncate max-w-[240px]"
                  title={String(v)}
                >
                  {truncate(v, 40)}
                </span>
              ))}
              {added.length > 8 && (
                <button
                  type="button"
                  data-testid={`diff-field-more-added-${field}`}
                  onClick={() => setShowFull(true)}
                  className="text-purpleColor hover:underline"
                >
                  +{added.length - 8} more
                </button>
              )}
            </div>
          )}
          {removed.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="flex items-center gap-1 text-redColor mr-1">
                <Minus size={12} /> {removed.length}
              </span>
              {removed.slice(0, 8).map((v, i) => (
                <span
                  key={i}
                  className="px-1.5 py-0.5 rounded bg-redColor/10 text-redColor border border-redColor/20 line-through truncate max-w-[240px]"
                  title={String(v)}
                >
                  {truncate(v, 40)}
                </span>
              ))}
              {removed.length > 8 && (
                <button
                  type="button"
                  data-testid={`diff-field-more-removed-${field}`}
                  onClick={() => setShowFull(true)}
                  className="text-purpleColor hover:underline"
                >
                  +{removed.length - 8} more
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <DiffListModal
        open={showFull}
        onClose={() => setShowFull(false)}
        field={field}
        variant={isList ? 'list' : 'text'}
        added={added}
        removed={removed}
        currentValue={currentValue}
        nextValue={newValue}
      />
    </div>
  );
};
